import BulkFacultyPrintModal from "./BulkFacultyPrintModal";
import BulkStudentCardDownloader from "./BulkStudentCardDownloader";
import PatronCard from "./PatronCard";
import { profileApi } from "@api/profile/profile";
import { userApi } from "@api/users/user";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { Icon, Button, Grid, Menu, Label } from "semantic-ui-react";

export const PatronResultsList = ({
  results,
  renderEmptyResultsElement,
  showAllResults = false,
  itemsPerPage = 15,
}) => {
  const [profileImages, setProfileImages] = useState({});
  const [imagesLoading, setImagesLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("all");

  // State for bulk faculty printing
  const [showBulkPrintModal, setShowBulkPrintModal] = useState(false);
  const [facultyDataForPrint, setFacultyDataForPrint] = useState([]);
  const [loadingFacultyData, setLoadingFacultyData] = useState(false);

  // State for bulk student card download
  const [showBulkStudentModal, setShowBulkStudentModal] = useState(false);
  const [studentDataForPrint, setStudentDataForPrint] = useState([]);
  const [loadingStudentData, setLoadingStudentData] = useState(false);

  // Add ref to track component mount status
  const isMountedRef = useRef(true);

  // Enhanced filter options including patron types
  const filterOptions = [
    { key: "all", value: "all", text: "All Users", icon: "users" },
    {
      key: "admin",
      value: "admin",
      text: "Admin",
      icon: "shield",
      color: "red",
    },
    {
      key: "librarian",
      value: "librarian",
      text: "Librarian",
      icon: "book",
      color: "teal",
    },
    {
      key: "faculty",
      value: "faculty",
      text: "Faculty",
      icon: "graduation cap",
      color: "purple",
    },
    {
      key: "researcher",
      value: "researcher",
      text: "Researcher",
      icon: "lab",
      color: "orange",
    },
    {
      key: "visitor",
      value: "visitor",
      text: "Visitor",
      icon: "eye",
      color: "brown",
    },
    {
      key: "student",
      value: "student",
      text: "Student",
      icon: "student",
      color: "blue",
    },
  ];

  // User type detection using database fields
  const getUserType = (patron) => {
    const userType = patron.metadata.user_type;
    const patronType = patron.metadata.patron_type;
    const role = patron.metadata.role;

    // 1. Check user_type field first (highest priority)
    if (userType) {
      const normalizedUserType = userType.trim().toLowerCase();
      return normalizedUserType;
    }

    // 2. Check role field (second priority)
    if (role) {
      return role.trim().toLowerCase();
    }

    // 3. Fallback to patron_type field (third priority)
    if (patronType) {
      const normalizedPatronType = patronType.trim().toLowerCase();
      return normalizedPatronType;
    }

    // 4. Default fallback
    return "student";
  };

  const isValidPatron = (patron) => {
    // List of known problematic user IDs
    const problematicUserIds = ["2", "7", "12", "13", "14", "15", "41"];

    // Check if this is a problematic user
    if (problematicUserIds.includes(patron?.metadata?.id)) {
      return false;
    }

    // Check if patron has basic required metadata including Patron ID
    const hasBasicData =
      patron?.metadata?.id &&
      patron?.metadata?.name &&
      patron?.metadata?.email &&
      patron?.metadata?.pid &&
      patron?.metadata?.pid !== "Not assigned" &&
      patron?.metadata?.pid.trim() !== "";
    return hasBasicData;
  };

  // Enhanced sorting function
  const getSortPriority = (patron) => {
    const userType = getUserType(patron);

    // Define sorting priority (lower number = higher priority)
    const priorities = {
      admin: 1,
      librarian: 2,
      faculty: 3,
      researcher: 4,
      visitor: 5,
      student: 6,
    };

    return priorities[userType] || 999;
  };

  // Function to filter results based on active filter
  const getFilteredResults = (results) => {
    if (!results) return [];

    // First filter out invalid patrons
    const validResults = results.filter(isValidPatron);

    // Then apply user type filter
    if (activeFilter === "all") {
      return validResults;
    }

    return validResults.filter((patron) => {
      const userType = getUserType(patron);
      return userType === activeFilter;
    });
  };

  // Function to count users by type (updated for new getUserType)
  const getUserTypeCounts = (results) => {
    if (!results) return {};

    const validResults = results.filter(isValidPatron);

    const counts = {
      all: validResults.length,
      admin: 0,
      librarian: 0,
      faculty: 0,
      researcher: 0,
      visitor: 0,
      student: 0,
    };

    validResults.forEach((patron) => {
      const userType = getUserType(patron);
      if (Object.prototype.hasOwnProperty.call(counts, userType)) {
        counts[userType]++;
      }
    });

    return counts;
  };

  // Check if user is faculty
  const isFaculty = (patron) => {
    const userType = getUserType(patron);
    return userType === "faculty";
  };

  // Check if user is student
  const isStudent = (patron) => {
    const userType = getUserType(patron);
    return userType === "student";
  };

  // Fetch faculty data for bulk printing
  const fetchFacultyDataForPrint = async () => {
    setLoadingFacultyData(true);

    try {
      const filteredResults = getFilteredResults(results);
      const facultyPatrons = filteredResults.filter(isFaculty);

      if (facultyPatrons.length === 0) {
        setFacultyDataForPrint([]);
        setLoadingFacultyData(false);
        return;
      }

      // Fetch all profiles, catching errors silently
      const facultyDataPromises = facultyPatrons.map(async (patron) => {
        try {
          const profileResponse = await userApi.getUserProfileById(
            patron.metadata.id
          );
          return {
            ...profileResponse.data,
            id: patron.metadata.id,
            name: patron.metadata.name,
            email: patron.metadata.email,
            patron_id: patron.metadata.pid,
            library_id: profileResponse.data.library_id || null,
          };
        } catch (error) {
          return null;
        }
      });

      const facultyResults = await Promise.all(facultyDataPromises);

      const validFaculty = facultyResults.filter((faculty) => faculty !== null);

      setFacultyDataForPrint(validFaculty);
    } catch (error) {
      console.error("Error fetching faculty data:", error);
      setFacultyDataForPrint([]);
    } finally {
      setLoadingFacultyData(false);
    }
  };

  // Fetch student data for bulk card download
  const fetchStudentDataForPrint = async () => {
    setLoadingStudentData(true);

    try {
      const filteredResults = getFilteredResults(results);
      const studentPatrons = filteredResults.filter(isStudent);

      if (studentPatrons.length === 0) {
        setStudentDataForPrint([]);
        setLoadingStudentData(false);
        return;
      }

      // Fetch all profiles, catching errors silently
      const studentDataPromises = studentPatrons.map(async (patron) => {
        try {
          const profileResponse = await userApi.getUserProfileById(
            patron.metadata.id
          );
          return {
            ...profileResponse.data,
            id: patron.metadata.id,
            name: patron.metadata.name,
            email: patron.metadata.email,
            patron_id: patron.metadata.pid,
            library_id: profileResponse.data.library_id || null,
            library_no:
              profileResponse.data.library_no ||
              profileResponse.data.library_id ||
              null,
            department: profileResponse.data.department || null,
            image_url: profileResponse.data.image_url || null,
          };
        } catch (error) {
          return null;
        }
      });

      const studentResults = await Promise.all(studentDataPromises);

      // Filter out null entries (failed/invalid users)
      const validStudents = studentResults.filter(
        (student) => student !== null
      );

      setStudentDataForPrint(validStudents);
    } catch (error) {
      setStudentDataForPrint([]);
    } finally {
      setLoadingStudentData(false);
    }
  };

  // Handle bulk faculty print
  const handleBulkPrint = async () => {
    setShowBulkPrintModal(true);
    await fetchFacultyDataForPrint();
  };

  // Handle bulk student card download
  const handleBulkStudentDownload = async () => {
    setShowBulkStudentModal(true);
    await fetchStudentDataForPrint();
  };

  // Check if bulk print should be shown for faculty
  const shouldShowBulkPrint = () => {
    if (activeFilter !== "faculty") return false;

    const filteredResults = getFilteredResults(results);
    const facultyCount = filteredResults.filter(isFaculty).length;

    return facultyCount > 0;
  };

  // Check if bulk download should be shown for students
  const shouldShowBulkStudentDownload = () => {
    if (activeFilter !== "student") return false;

    const filteredResults = getFilteredResults(results);
    const studentCount = filteredResults.filter(isStudent).length;

    return studentCount > 0;
  };

  // Cleanup function to mark component as unmounted
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  // Fetch profile images for all patrons when results change
  useEffect(() => {
    const fetchProfileImages = async () => {
      if (!results || results.length === 0) {
        if (isMountedRef.current) {
          setImagesLoading(false);
        }
        return;
      }

      try {
        const userIds = results
          .map((patron) => patron.metadata.id)
          .filter((id) => id);

        if (userIds.length === 0) {
          if (isMountedRef.current) {
            setImagesLoading(false);
          }
          return;
        }

        const response = await profileApi.getBatchUserImages(userIds);

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setProfileImages(response.images || {});
        }
      } catch (error) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setProfileImages({});
        }
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setImagesLoading(false);
        }
      }
    };

    if (isMountedRef.current) {
      setImagesLoading(true);
    }
    fetchProfileImages();
  }, [results]);

  // Handle empty results
  if (!results || results.length === 0) {
    return renderEmptyResultsElement ? (
      renderEmptyResultsElement()
    ) : (
      <div
        style={{
          textAlign: "center",
          padding: "40px 20px",
          color: "#999",
        }}
      >
        <Icon name="users" size="huge" />
        <h3>No patrons found</h3>
        <p>Try adjusting your search criteria.</p>
      </div>
    );
  }

  // Show loading state while fetching images
  if (imagesLoading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px 20px",
          color: "#999",
        }}
      >
        <Icon name="spinner" loading size="huge" />
        <h3>Loading patrons...</h3>
      </div>
    );
  }

  // Helper function to extract surname (last name) from full name
  const getSurname = (fullName) => {
    if (!fullName) return "";
    const nameParts = fullName.trim().split(" ");
    return nameParts[nameParts.length - 1] || "";
  };

  // Get filtered results
  const filteredResults = getFilteredResults(results);

  // Enhanced sorting: first by role/patron type priority, then alphabetically by surname
  const sortedResults = [...filteredResults].sort((a, b) => {
    // First sort by role/patron type priority
    const priorityA = getSortPriority(a);
    const priorityB = getSortPriority(b);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If same priority, sort alphabetically by surname
    const surnameA = getSurname(a.metadata.name).toLowerCase();
    const surnameB = getSurname(b.metadata.name).toLowerCase();

    if (surnameA === surnameB) {
      // If surnames are the same, sort by first name
      const firstNameA = (a.metadata.name || "").toLowerCase();
      const firstNameB = (b.metadata.name || "").toLowerCase();
      return firstNameA.localeCompare(firstNameB);
    }

    return surnameA.localeCompare(surnameB);
  });

  // Apply pagination if showAllResults is false
  const displayResults = showAllResults
    ? sortedResults
    : sortedResults.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const totalPages = showAllResults
    ? 1
    : Math.ceil(sortedResults.length / itemsPerPage);

  // Group display results into rows of 3
  const groupedPatrons = [];
  for (let i = 0; i < displayResults.length; i += 3) {
    groupedPatrons.push(displayResults.slice(i, i + 3));
  }

  // Get user type counts for filter labels
  const userTypeCounts = getUserTypeCounts(results);

  return (
    <div
      className="patron-cards-container"
      style={{ margin: "0", padding: "0" }}
    >
      {/* Enhanced Filter Menu */}
      <div style={{ marginBottom: "1.5rem" }}>
        <Menu pointing secondary>
          {filterOptions.map((option) => (
            <Menu.Item
              key={option.key}
              name={option.value}
              active={activeFilter === option.value}
              onClick={() => setActiveFilter(option.value)}
            >
              <Icon name={option.icon} />
              {option.text}
              <Label
                color={option.color || "grey"}
                size="small"
                style={{ marginLeft: "8px" }}
              >
                {userTypeCounts[option.value] || 0}
              </Label>
            </Menu.Item>
          ))}
        </Menu>

        {/* Bulk Action Buttons Container */}
        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          {/* Bulk Print Button for Faculty */}
          {shouldShowBulkPrint() && (
            <Button
              color="purple"
              icon="print"
              content="Bulk Print Faculty Library IDs"
              onClick={handleBulkPrint}
              size="small"
            />
          )}

          {/* Bulk Download Button for Students */}
          {shouldShowBulkStudentDownload() && (
            <Button
              color="blue"
              icon="download"
              content="Bulk Download Student Library Cards"
              onClick={handleBulkStudentDownload}
              size="small"
            />
          )}
        </div>
      </div>

      {/* Handle filtered empty results */}
      {sortedResults.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px 20px",
            color: "#999",
          }}
        >
          <Icon name="filter" size="huge" />
          <h3>
            No{" "}
            {activeFilter === "all" ? "users" : activeFilter.replace("-", " ")}{" "}
            found
          </h3>
          <p>
            Try selecting a different filter or adjusting your search criteria.
          </p>
        </div>
      ) : (
        <>
          {/* Show total count */}
          {!showAllResults && (
            <div style={{ marginBottom: "1rem", color: "#666" }}>
              Showing {(currentPage - 1) * itemsPerPage + 1}-
              {Math.min(currentPage * itemsPerPage, sortedResults.length)} of{" "}
              {sortedResults.length}{" "}
              {activeFilter === "all"
                ? "patrons"
                : activeFilter.replace("-", " ") + "s"}
            </div>
          )}

          {groupedPatrons.map((patronGroup, rowIndex) => (
            <Grid
              key={rowIndex}
              columns={3}
              stackable
              style={{ marginBottom: "1em", marginLeft: "0", marginRight: "0" }}
            >
              {patronGroup.map((patron, cardIndex) => (
                <Grid.Column
                  key={`${rowIndex}-${cardIndex}`}
                  style={{ paddingLeft: "0.5em", paddingRight: "0.5em" }}
                >
                  <PatronCard patron={patron} profileImages={profileImages} />
                </Grid.Column>
              ))}
              {/* Fill empty columns if the last row has fewer than 3 cards */}
              {patronGroup.length < 3 &&
                Array.from({ length: 3 - patronGroup.length }).map(
                  (_, emptyIndex) => (
                    <Grid.Column
                      key={`empty-${rowIndex}-${emptyIndex}`}
                      style={{ paddingLeft: "0.5em", paddingRight: "0.5em" }}
                    />
                  )
                )}
            </Grid>
          ))}

          {/* Frontend pagination controls */}
          {!showAllResults && totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "2rem",
                gap: "1rem",
              }}
            >
              <Button
                icon="chevron left"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                basic
              />

              <div style={{ display: "flex", gap: "0.5rem" }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page =
                    currentPage <= 3
                      ? i + 1
                      : currentPage >= totalPages - 2
                      ? totalPages - 4 + i
                      : currentPage - 2 + i;

                  if (page < 1 || page > totalPages) return null;

                  return (
                    <Button
                      key={page}
                      size="small"
                      basic={page !== currentPage}
                      primary={page === currentPage}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>

              <Button
                icon="chevron right"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                basic
              />
            </div>
          )}
        </>
      )}

      {/* Bulk Faculty Print Modal */}
      <BulkFacultyPrintModal
        open={showBulkPrintModal}
        onClose={() => setShowBulkPrintModal(false)}
        facultyData={facultyDataForPrint}
        loading={loadingFacultyData}
      />

      {/* Bulk Student Card Download Modal */}
      <BulkStudentCardDownloader
        open={showBulkStudentModal}
        onClose={() => setShowBulkStudentModal(false)}
        studentData={studentDataForPrint}
        loading={loadingStudentData}
      />
    </div>
  );
};

PatronResultsList.propTypes = {
  results: PropTypes.array.isRequired,
  renderEmptyResultsElement: PropTypes.func,
  showAllResults: PropTypes.bool,
  itemsPerPage: PropTypes.number,
};

PatronResultsList.defaultProps = {
  renderEmptyResultsElement: null,
  showAllResults: false,
  itemsPerPage: 15,
};
